import { ActivateButton } from './ActivateButton'
import { BorderProps } from 'styled-system'
import { Box, Paper, Stack, Typography } from '@dtx-company/design-system/src'
import { DeleteModeDrawer } from './DeleteModeDrawer'
import { FC, ReactNode } from 'react'
import { Image } from '@dtx-company/shared-components/src/components/atoms/Image'
import { ImageStyleProps } from '@dtx-company/shared-components/src/types/atoms/image.types'
import { LinkEditSection } from '../../../../components/profile/Links/LinkEditSection'
import { LinkType } from '@dtx-company/flow-codegen/src/page/typeUtils'
import { LockedTemplateField } from '../../../FlowpageTemplates/LockedTemplateField/LockedTemplateField'
import { ManagerSettings } from './ManagerSettings'
import { indexInterfaceWithVar } from '@dtx-company/true-common/src/utils/objects'
import { useManagerSettings } from './useManagerSettings'
import { useScreenSizeBreakpoints } from '@dtx-company/app/shared/common/src/hooks/useScreenSizeBreakpoints'
import _Image from 'next/image'
import styled from 'styled-components'
import ButtonBase from '@mui/material/ButtonBase'

export const InlineManagerCard: FC<{ viewOnly?: boolean; children: ReactNode }> = ({
  children,
  viewOnly
}) => {
  return (
    <Paper variant={viewOnly ? 'outlined' : 'elevation'} sx={{ width: '100%' }}>
      {children}
    </Paper>
  )
}

export interface ManagerProps extends BorderProps {
  link: LinkType
  key?: string
  empty?: boolean
  hideIcon?: boolean
  dataCy?: string | null
  editWidget: () => void
  iconProps?: Partial<ImageStyleProps>
  handle?: ReactNode
  iconUrl?: string
  altTitle?: string
  customTitleSection?: JSX.Element
  children?: ReactNode
}

export const AnimatedImage = styled(Image)<ImageStyleProps & { isOpen: boolean }>`
  transform: ${props => (props.isOpen ? `rotate(180deg)` : 'rotate(0deg)')};
  transition: transform 0.25s;
`

export const DefaultManager: FC<ManagerProps> = ({
  link,
  altTitle,
  editWidget,
  empty = false,
  handle,
  children,
  iconUrl,
  customTitleSection
}) => {
  const {
    hasViewOnlyAccess,
    onDeleteLink,
    deleteLinkModeActive,
    toggleDeleteMode,
    isActive,
    toggleActivate,
    showEditLink,
    setShowEditLink,
    toggleEditMode,
    childOnClick,
    isTemplateConfigLocked
  } = useManagerSettings(link, editWidget, empty)

  const { isMobile } = useScreenSizeBreakpoints()

  const { description: rootDescription, thumbNailImgUrl, title, embed } = link

  const description = rootDescription?.length
    ? rootDescription
    : (link.actionData?.items?.[0]?.description ?? '')

  const url = indexInterfaceWithVar(link.actionData, 'link')
  const hasNonDefaultImage = thumbNailImgUrl && thumbNailImgUrl !== '/icons/social-media/blank.png'

  const Handle = (): ReactNode => {
    if (!hasViewOnlyAccess && !isTemplateConfigLocked && handle) {
      return handle
    } else {
      return <Box sx={{ width: 32, height: 32 }} />
    }
  }

  return (
    <InlineManagerCard viewOnly={hasViewOnlyAccess || isTemplateConfigLocked}>
      {/* Prevent a duplicate form from rendering inline (below the preview) on mobile,
      in addition to the form in drawer that comes up from the bottom of the screen */}
      {showEditLink && !isMobile ? (
        <LinkEditSection handleClose={() => setShowEditLink(false)} />
      ) : (
        <Stack justifyContent="space-between" alignItems="center">
          <Stack
            justifyContent="space-between"
            sx={{ p: 4, overflow: 'hidden', width: '100%' }}
            alignItems="center"
            direction="row"
            gap={4}
          >
            {!isTemplateConfigLocked && (
              <ManagerSettings
                onClickEdit={toggleEditMode}
                onClickDelete={toggleDeleteMode}
                disabled={hasViewOnlyAccess}
                viewOnly={hasViewOnlyAccess}
              />
            )}

            {isTemplateConfigLocked && <LockedTemplateField />}

            {hasNonDefaultImage || iconUrl ? (
              <_Image
                alt=""
                width={120}
                height={120}
                style={{
                  height: 60,
                  width: 60,
                  borderRadius: link.actionData?.widgetDisplayType === 'circle' ? '50%' : '4px',
                  objectFit: link.displayType === 'featured' ? 'contain' : 'cover'
                }}
                src={hasNonDefaultImage ? thumbNailImgUrl : iconUrl || ''}
              />
            ) : (
              <Box sx={{ width: 60, height: 60 }} />
            )}
            <ButtonBase component={Stack} disabled={hasViewOnlyAccess} onClick={childOnClick}>
              {customTitleSection ? (
                customTitleSection
              ) : (
                <Stack sx={{ width: '350px' }} justifyContent="center" gap={1}>
                  <Typography lineHeight={1} variant="h4">
                    {embed && url ? url : title || altTitle}
                  </Typography>
                  <Typography lineHeight={1} textOverflow="ellipsis" variant="body2">
                    {description}
                  </Typography>
                </Stack>
              )}
            </ButtonBase>
            <Stack direction="row" gap={4} alignItems="center">
              <ActivateButton
                viewOnly={hasViewOnlyAccess || isTemplateConfigLocked}
                toggleActivated={toggleActivate}
                activated={isActive}
              />
              {!hasViewOnlyAccess && <Handle />}
            </Stack>
          </Stack>
          {children}
        </Stack>
      )}

      {!showEditLink && (
        <DeleteModeDrawer
          deleteModeActive={deleteLinkModeActive}
          toggleDeleteMode={toggleDeleteMode}
          onDelete={onDeleteLink}
        />
      )}
    </InlineManagerCard>
  )
}
